import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { Sidebar } from './components/sidebar';
import { PDFViewerContainer } from './components/pdf-viewer-container';
import { QuizView } from './components/quiz-view';
import { NotesView } from './components/notes-view';
import { ProfileView } from './components/profile-view';
import { SettingsView } from './components/settings-view';
import { HelpView } from './components/help-view';
import { MobileBottomNav } from './components/mobile-bottom-nav';
import { MobileHeader } from './components/mobile-header';
import { MobileMenuDrawer } from './components/mobile-menu-drawer';
import { LibraryContextMenu } from './components/library-context-menu';
import { ChallengeContextMenu } from './components/challenge-context-menu';
import { NotesContextMenu } from './components/notes-context-menu';
import { ProfileContextMenu } from './components/profile-context-menu';
import { LandingPage } from './components/landing-page';
import { ChatPanel } from './components/chat-panel';
import { OnboardingModal, useOnboarding } from './components/onboarding-modal';
import { UploadProgressOverlay } from './components/upload-progress-overlay';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from './components/ui/resizable';
import SourcityLogo from '../imports/SourcityLogoWordmarkIconEmbedded1';
import { useLibraryStore } from '../stores/libraryStore';
import { useUserStore } from '../stores/userStore';
import { useNotesStore } from '../stores/notesStore';
import { useQuizStore } from '../stores/quizStore';
import { Toaster } from './components/ui/sonner';
import { useI18n } from '../i18n';
import { LanguageToggle } from './components/language-toggle';
import { readBillingNavigation, type BillingPlan } from '../billing/checkout-return';
import { useBillingReturn } from '../hooks/use-billing-return';
import { WhatsAppSupport } from './components/whatsapp-support';

type TabType = 'library' | 'quiz' | 'notes' | 'profile';
type AuthView = 'landing' | 'sign-in' | 'sign-up';

export default function App() {
  const { t, isRTL, lang } = useI18n();
  const [billingNavigation, setBillingNavigation] = useState(() => readBillingNavigation(window.location.search));
  const { isLoaded, isSignedIn, getToken, userId } = useAuth();
  const authOwner = useRef<string | null | undefined>(undefined);
  const changingAccount = useRef(false);
  
  const [activeTab, setActiveTab] = useState<TabType>('library');
  const [sidebarWidth, setSidebarWidth] = useState(20);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLibraryMenuOpen, setIsLibraryMenuOpen] = useState(false);
  const [isChallengeMenuOpen, setIsChallengeMenuOpen] = useState(false);
  const [createMobileChallenge, setCreateMobileChallenge] = useState(false);
  const [isNotesMenuOpen, setIsNotesMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [authView, setAuthView] = useState<AuthView>(billingNavigation.returnTo ? 'sign-in' : billingNavigation.plan ? 'sign-up' : 'landing');
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Onboarding
  const { showOnboarding, closeOnboarding } = useOnboarding();

  // Stores
  const {
    fetchLibrary,
    selectedMaterial,
    showUploadOverlay,
    uploadProgress,
    uploadPhase,
    uploadStage,
    dismissUploadOverlay,
  } = useLibraryStore();
  const { fetchProfile, fetchLimits, clearProfile } = useUserStore();
  const { fetchAllNotes } = useNotesStore();
  const hasGeneratedChallenge = useQuizStore(state => state.hasGenerated);
  useLayoutEffect(() => {
    // An unresolved Clerk session is not proof of sign-out. Preserve the first login return.
    if (!isLoaded || (isSignedIn && !userId)) return;
    const next = isSignedIn ? userId! : null;
    const previous = authOwner.current;
    authOwner.current = next;
    if (previous !== next) clearProfile();
    if (typeof previous === 'string' && previous !== next) {
      changingAccount.current = true;
      setBillingNavigation({ plan: null, returnTo: null });
      setShowProfile(false);
      setAuthView('landing');
      const url = new URL(window.location.href);
      for (const key of ['checkout', 'session_id', 'billing', 'billingPlan']) url.searchParams.delete(key);
      window.history.replaceState(null, '', url);
      // Start the next account with fresh library, notes, reader and billing stores.
      // A single navigation avoids letting any previous account's request publish late data.
      window.location.replace(url.href);
    }
  }, [isLoaded, isSignedIn, userId, clearProfile]);
  const refreshBillingUser = useCallback(async (token: string, canCommit: () => boolean) => {
    const current = () => !!userId && authOwner.current === userId && !changingAccount.current && canCommit();
    await Promise.all([fetchProfile(token, current), fetchLimits(token, current)]);
  }, [userId, fetchProfile, fetchLimits]);
  const billingReturn = useBillingReturn(billingNavigation.returnTo, isSignedIn && userId ? userId : undefined, getToken, refreshBillingUser);
  useEffect(() => {
    if (isSignedIn && (billingNavigation.returnTo || billingNavigation.plan)) setShowProfile(true);
  }, [isSignedIn, billingNavigation]);
  const authReturnUrl = `${window.location.pathname}${window.location.search}`;


  // Load initial data when signed in
  useEffect(() => {
    let cancelled = false;
    const current = () => !cancelled && !!userId && authOwner.current === userId && !changingAccount.current;
    const loadData = async () => {
      if (isLoaded && isSignedIn && userId) {
        const token = await getToken();
        if (token && current()) {
          fetchLibrary(token, current);
          fetchProfile(token, current);
          fetchLimits(token, current);
          fetchAllNotes(token);
        }
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, userId, getToken, fetchLibrary, fetchProfile, fetchLimits, fetchAllNotes]);

  const openMobileChallenges = (createNew = false) => {
    closeAllOverlays();
    setIsMobileMenuOpen(false);
    setIsLibraryMenuOpen(false);
    setIsNotesMenuOpen(false);
    setIsProfileMenuOpen(false);
    setActiveTab('quiz');
    setCreateMobileChallenge(createNew);
    setIsChallengeMenuOpen(true);
  };

  // Handle tab change for mobile
  const handleMobileTabChange = (tab: TabType) => {
    // Check if we're on an overlay (settings, help, or profile)
    const isOnOverlay = showSettings || showHelp || showProfile;
    if (tab === 'quiz' && (!hasGeneratedChallenge || (!isOnOverlay && activeTab === 'quiz'))) {
      openMobileChallenges();
      return;
    }
    
    // If tapping the same tab we're already on (and not on an overlay), show context menu
    if (!isOnOverlay) {
      if (tab === 'library' && activeTab === 'library') {
        setIsLibraryMenuOpen(true);
        return;
      }
      if (tab === 'notes' && activeTab === 'notes') {
        setIsNotesMenuOpen(true);
        return;
      }
    }
    
    // If on profile and tapping profile again, show profile context menu
    if (tab === 'profile' && showProfile) {
      setIsProfileMenuOpen(true);
      return;
    }

    // Navigate to the selected tab
    if (tab === 'profile') {
      closeAllOverlays();
      setShowProfile(true);
    } else {
      closeAllOverlays();
      setActiveTab(tab);
    }
  };

  // Handle tab change for desktop
  const handleDesktopTabChange = (tab: 'library' | 'quiz' | 'notes') => {
    setActiveTab(tab);
    setShowProfile(false);
  };

  // Handle auth actions
  const handleGetStarted = (plan?: BillingPlan) => {
    if (plan) {
      const url = new URL(window.location.href);
      url.searchParams.set('billingPlan', plan);
      url.searchParams.set('lang', lang);
      window.history.replaceState(null, '', url);
      setBillingNavigation(readBillingNavigation(url.search));
    }
    setAuthView('sign-up');
  };

  const handleSignIn = () => {
    setAuthView('sign-in');
  };

  // Close all overlay views
  const closeAllOverlays = () => {
    setShowProfile(false);
    setShowSettings(false);
    setShowHelp(false);
  };

  // Handlers for navigating to settings/help from other components
  const handleShowSettings = () => {
    closeAllOverlays();
    setShowSettings(true);
  };

  const handleShowHelp = () => {
    closeAllOverlays();
    setShowHelp(true);
  };

  const handleShowProfile = () => {
    closeAllOverlays();
    setShowProfile(true);
  };

  // Render content based on active tab
  const renderContent = () => {
    if (showSettings) return <SettingsView onClose={() => setShowSettings(false)} />;
    if (showHelp) return <HelpView onClose={() => setShowHelp(false)} />;
    if (showProfile) return <ProfileView onClose={() => setShowProfile(false)} onShowSettings={handleShowSettings} selectedPlan={billingNavigation.plan} billingProgress={billingReturn.progress} onRetryBilling={billingReturn.retry} />;
    if (activeTab === 'library') return <PDFViewerContainer />;
    if (activeTab === 'quiz') return <QuizView />;
    if (activeTab === 'notes') return <NotesView />;
    return <PDFViewerContainer />;
  };

  // Loading state
  const resolvedOwner = isLoaded && (!isSignedIn || userId) ? (isSignedIn ? userId : null) : undefined;
  const ownerChanged = typeof authOwner.current === 'string' && resolvedOwner !== undefined && authOwner.current !== resolvedOwner;
  if (!isLoaded || (isSignedIn && !userId) || ownerChanged || changingAccount.current) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-white to-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <SourcityLogo />
          </div>
          <div className="animate-pulse text-[#434343]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // Not signed in - show landing or auth
  if (!isSignedIn) {
    if (authView === 'sign-in') {
      return (
        <>
          <OnboardingModal isOpen={showOnboarding} onClose={closeOnboarding} />
          <WhatsAppSupport floating />
          <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-white via-[#F5F5F7] to-white">
            <div className="w-full max-w-md p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
              <button 
                onClick={() => setAuthView('landing')}
                className="text-sm text-[#7367FF] hover:underline"
              >
                {t('auth.backToHome')}
              </button>
              <LanguageToggle />
              </div>
              <SignIn 
                appearance={{
                  elements: {
                    rootBox: 'w-full',
                    card: 'bg-white/80 backdrop-blur-xl shadow-xl border border-white/40 rounded-2xl',
                    headerTitle: 'text-[#434343]',
                    headerSubtitle: 'text-[#434343]/60',
                    socialButtonsBlockButton: 'border-white/40 hover:bg-white/60',
                    formFieldInput: 'border-white/40 focus:border-[#7367FF]',
                    formButtonPrimary: 'bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90',
                    footer: 'hidden',
                  }
                }}
                forceRedirectUrl={authReturnUrl}
              />
              <p className="mt-4 text-center text-sm text-[#434343]/60">
                {t('auth.noAccount')}{' '}
                <button onClick={() => setAuthView('sign-up')} className="text-[#7367FF] hover:underline">
                  {t('common.signUp')}
                </button>
              </p>
            </div>
          </div>
        </>
      );
    }

    if (authView === 'sign-up') {
      return (
        <>
          <OnboardingModal isOpen={showOnboarding} onClose={closeOnboarding} />
          <WhatsAppSupport floating />
          <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-white via-[#F5F5F7] to-white">
            <div className="w-full max-w-md p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
              <button 
                onClick={() => setAuthView('landing')}
                className="text-sm text-[#7367FF] hover:underline"
              >
                {t('auth.backToHome')}
              </button>
              <LanguageToggle />
              </div>
              <SignUp 
                appearance={{
                  elements: {
                    rootBox: 'w-full',
                    card: 'bg-white/80 backdrop-blur-xl shadow-xl border border-white/40 rounded-2xl',
                    headerTitle: 'text-[#434343]',
                    headerSubtitle: 'text-[#434343]/60',
                    socialButtonsBlockButton: 'border-white/40 hover:bg-white/60',
                    formFieldInput: 'border-white/40 focus:border-[#7367FF]',
                    formButtonPrimary: 'bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90',
                    footer: 'hidden',
                  }
                }}
                forceRedirectUrl={authReturnUrl}
              />
              <p className="mt-4 text-center text-sm text-[#434343]/60">
                {t('auth.haveAccount')}{' '}
                <button onClick={() => setAuthView('sign-in')} className="text-[#7367FF] hover:underline">
                  {t('common.signIn')}
                </button>
              </p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <OnboardingModal isOpen={showOnboarding} onClose={closeOnboarding} />
        <LandingPage onGetStarted={handleGetStarted} />
        <WhatsAppSupport floating />
      </>
    );
  }

  // Signed in - show main app
  return (
    <div className="flex h-screen w-full overflow-hidden bg-white relative">
      <Toaster position={isRTL ? 'top-left' : 'top-right'} />
      {showUploadOverlay && (
        <UploadProgressOverlay
          progress={uploadProgress}
          phase={uploadPhase}
          stage={uploadStage}
          onDismiss={dismissUploadOverlay}
        />
      )}
      
      {/* Onboarding Modal */}
      <OnboardingModal isOpen={showOnboarding} onClose={closeOnboarding} />
      
      {/* Chat Panel - available on all views when a document is selected */}
      <ChatPanel isOpen={isChatOpen} onToggle={() => setIsChatOpen(!isChatOpen)} />
      
      {/* Mobile Layout (< 768px) */}
      <div className="md:hidden flex flex-col h-full w-full">
        <MobileHeader onMenuClick={() => setIsMobileMenuOpen(true)} />
        
        <div className="flex-1 mt-[56px] mb-[72px] min-h-0 overflow-hidden">
          {renderContent()}
        </div>
        
        <MobileBottomNav 
          activeTab={activeTab} 
          onTabChange={handleMobileTabChange}
          onAskAI={() => setIsChatOpen(!isChatOpen)}
          isChatOpen={isChatOpen}
        />
        
        <MobileMenuDrawer
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          onShowProfile={handleShowProfile}
          onShowChallenges={openMobileChallenges}
          onShowSettings={handleShowSettings}
          onShowHelp={handleShowHelp}
        />
        
        <LibraryContextMenu
          isOpen={isLibraryMenuOpen}
          onClose={() => setIsLibraryMenuOpen(false)}
        />
        
        <ChallengeContextMenu
          isOpen={isChallengeMenuOpen}
          onClose={() => setIsChallengeMenuOpen(false)}
        >
          {isChallengeMenuOpen && (
            <Sidebar activeTab="quiz" sidebarWidth={100} contentOnly initialCreateQuiz={createMobileChallenge}
              onShowProfile={() => { setIsChallengeMenuOpen(false); handleShowProfile(); }}
              onTabChange={() => {
                closeAllOverlays();
                setActiveTab('quiz');
                setIsChallengeMenuOpen(false);
              }} />
          )}
        </ChallengeContextMenu>
        
        <NotesContextMenu
          isOpen={isNotesMenuOpen}
          onClose={() => setIsNotesMenuOpen(false)}
        />
        
        <ProfileContextMenu
          isOpen={isProfileMenuOpen}
          onClose={() => setIsProfileMenuOpen(false)}
          onShowSettings={handleShowSettings}
          onShowHelp={handleShowHelp}
        />
      </div>

      {/* Desktop Layout (≥ 768px) */}
      <div className="hidden md:flex h-full min-h-0 w-full">
        {(showProfile || showSettings || showHelp) ? (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            {renderContent()}
          </div>
        ) : isCollapsed ? (
          <>
            <button
              onClick={() => setIsCollapsed(false)}
              className="fixed start-4 top-4 z-50 w-12 h-12 flex items-center justify-center bg-white/60 backdrop-blur-xl border border-white/20 rounded-xl shadow-lg hover:bg-white/80 hover:shadow-xl transition-all duration-200 active:scale-95"
            >
              <div className="w-8 h-8">
                <SourcityLogo />
              </div>
            </button>
            
            <div className="flex-1">
              {renderContent()}
            </div>
          </>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="min-h-0">
            <ResizablePanel 
              className="min-h-0"
              defaultSize={20} 
              minSize={10} 
              maxSize={40}
              onResize={(size) => setSidebarWidth(size)}
            >
              <Sidebar 
                activeTab={activeTab === 'profile' ? 'library' : activeTab} 
                onTabChange={handleDesktopTabChange} 
                sidebarWidth={sidebarWidth}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(true)}
                onShowProfile={handleShowProfile}
                onShowSettings={handleShowSettings}
                onShowHelp={handleShowHelp}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel className="min-h-0" defaultSize={80}>
              {renderContent()}
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
    </div>
  );
}
